import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { PublicBookingWork } from '../../types/publicBooking';

interface WorkGalleryProps {
	/** The salon's published work, as the booking-page info call returns it. */
	work: PublicBookingWork[] | null | undefined;
}

/**
 * The salon's "See Our Work" gallery: completed-work photos the provider chose
 * to publish from approved task reports, each credited to the stylist who did
 * the work. Renders nothing at all while there is nothing published, so a
 * salon that has not started publishing sees its booking page unchanged.
 *
 * A tap opens the photo full-size in the same dark lightbox the stylist cards
 * use, with the stylist's name and the service as the caption.
 */
const WorkGallery = ({ work }: WorkGalleryProps) => {
	const photos = useMemo(() => (work ?? []).filter(item => Boolean(item.url)), [work]);
	const [index, setIndex] = useState<number | null>(null);
	const [failed, setFailed] = useState<Record<string, boolean>>({});

	if (photos.length === 0) return null;

	return (
		<div className="pb__card pb-gallery">
			<div className="pb__card-header">
				<div className="pb__card-title">See Our Work</div>
				<div className="pb__card-desc">Recent styles by our team</div>
			</div>

			<div className="pb-gallery__grid">
				{photos.map((photo, position) => (
					<button
						key={photo.url}
						type="button"
						className="pb-gallery__item"
						onClick={() => setIndex(position)}
						aria-label={`${photo.serviceName ?? 'Completed work'} by ${photo.staffName}`}
					>
						<img
							src={photo.url}
							alt=""
							loading="lazy"
							onError={() => setFailed(prev => ({ ...prev, [photo.url]: true }))}
						/>
						{photo.staffName && !failed[photo.url] && (
							<span className="pb-gallery__credit">{photo.staffName}</span>
						)}
					</button>
				))}
			</div>

			{index !== null && (
				<WorkLightbox
					photos={photos}
					index={index}
					onIndexChange={setIndex}
					onClose={() => setIndex(null)}
				/>
			)}
		</div>
	);
};

interface WorkLightboxProps {
	photos: PublicBookingWork[];
	index: number;
	onIndexChange: (index: number) => void;
	onClose: () => void;
}

/** Full-size view, on the .pb-work stage the stylist galleries already use. */
const WorkLightbox = ({ photos, index, onIndexChange, onClose }: WorkLightboxProps) => {
	const closeRef = useRef<HTMLButtonElement>(null);
	const touchStartX = useRef<number | null>(null);
	const hasMany = photos.length > 1;

	const step = useCallback(
		(direction: number) => {
			if (photos.length < 2) return;
			onIndexChange((index + direction + photos.length) % photos.length);
		},
		[index, photos.length, onIndexChange]
	);

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') onClose();
			if (event.key === 'ArrowLeft') step(-1);
			if (event.key === 'ArrowRight') step(1);
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [onClose, step]);

	useEffect(() => {
		const previous = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		closeRef.current?.focus();
		return () => {
			document.body.style.overflow = previous;
		};
	}, []);

	const current = photos[index];

	return (
		<div
			className="pb-work"
			role="presentation"
			onClick={event => {
				if (event.target === event.currentTarget) onClose();
			}}
		>
			<div className="pb-work__panel" role="dialog" aria-modal="true" aria-label="Our work">
				<div className="pb-work__header">
					<div className="pb-work__identity">
						<span>
							<span className="pb-work__name">{current.staffName || 'Our team'}</span>
							{current.serviceName && (
								<span className="pb-work__role">{current.serviceName}</span>
							)}
						</span>
					</div>
					<button
						ref={closeRef}
						type="button"
						className="pb-work__close"
						onClick={onClose}
						aria-label="Close"
					>
						×
					</button>
				</div>

				<div className="pb-work__body">
					<div
						className="pb-work__stage"
						onTouchStart={event => {
							touchStartX.current = event.touches[0].clientX;
						}}
						onTouchEnd={event => {
							if (touchStartX.current === null) return;
							const dx = event.changedTouches[0].clientX - touchStartX.current;
							touchStartX.current = null;
							if (dx > 45) step(-1);
							else if (dx < -45) step(1);
						}}
					>
						<img
							key={current.url}
							className="pb-work__photo"
							src={current.url}
							alt={`${current.serviceName ?? 'Completed work'} by ${current.staffName}, photo ${index + 1} of ${photos.length}`}
						/>

						{hasMany && (
							<>
								<button
									type="button"
									className="pb-work__nav pb-work__nav--prev"
									onClick={() => step(-1)}
									aria-label="Previous photo"
								>
									&#8249;
								</button>
								<button
									type="button"
									className="pb-work__nav pb-work__nav--next"
									onClick={() => step(1)}
									aria-label="Next photo"
								>
									&#8250;
								</button>
								<div className="pb-work__counter">
									{index + 1} / {photos.length}
								</div>
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default WorkGallery;
